'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { UnauthInfiniteQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

const PAGE_PARAM = 1;
const PAGE_SIZE = 3;

const RegionalBenefitSection = () => {
  const observerRef = useRef<HTMLDivElement>(null);
  const {
    data: events = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    ...UnauthInfiniteQueryOptions[
      'get:/wello/v2/allows/home/institution-event'
    ]({
      query: {
        excludeEndedYN: true,
        size: PAGE_SIZE,
        page: PAGE_PARAM,
      },
    }),
    select: ({ pages }) =>
      pages.flatMap(({ context }) => context?.contents ?? []),
    getNextPageParam: (lastPage) => {
      if (lastPage.context?.totalCount && lastPage.context.page) {
        const nextPage = lastPage.context.page + 1;
        const maxPage = Math.ceil(lastPage.context.totalCount / PAGE_SIZE);

        return nextPage <= maxPage ? nextPage : undefined;
      }
    },
    initialPageParam: PAGE_PARAM,
  });
  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '0px 0px 200px 0px' },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (!isLoading && events.length === 0) {
    return null;
  }

  return (
    <section className="flex w-full flex-col items-center gap-64 px-40 py-120 mobile:gap-32 mobile:px-20 mobile:py-60">
      {/* 타이틀 */}
      <div className="text-center font-800 typo-h2 mobile:font-700 mobile:typo-h5">
        <div>
          <span className="text-O-500">지역별 추가 혜택</span>
          <span className="text-Gray-black">도</span>
        </div>
        <p className="text-Gray-black">받아보세요!</p>
      </div>

      {/* 배너 리스트 */}
      <div className="flex w-full flex-col gap-24 mobile:gap-12">
        {isLoading
          ? Array.from({ length: PAGE_SIZE }, (_, index) => (
              <div
                key={index}
                className="aspect-[335/120] w-full rounded-medium bg-Gray-100"
              />
            ))
          : events.map((event) => (
              <CustomLink
                key={event.eventId}
                href={`${DONATION_ROUTES.MY_ETC_EVENT_DETAIL.pathname({
                  eventId: event.eventId,
                })}?utm_source=gogibu_guide&utm_medium=banner&utm_campaign=donation_event&utm_content=donation_event_2026`}
                className="relative block aspect-[335/120] w-full overflow-hidden rounded-medium"
              >
                <Image
                  src={event.bannerImageUrl}
                  alt={`${event.institutionName} 이벤트`}
                  fill
                  sizes="(max-width: 1279px) 100vw, 600px"
                  className="object-cover"
                />
              </CustomLink>
            ))}

        {/* 무한스크롤 트리거 */}
        {hasNextPage && <div ref={observerRef} className="h-4 w-full" />}
      </div>
    </section>
  );
};

export default RegionalBenefitSection;
